import React, {useRef} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import {CircularProgress, Stack} from '@mui/material';
import {Body, Button, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import RadialMenu, {RadialMenuItem} from '@/components/radial-menu';
import type {PendingAttachment} from '@/hooks/use-message-attachments/useMessageAttachments';
import styles from './ChatInput.module.scss';

const EMOJI_OPTIONS = ['👍', '👀', '🚩', '✅', '🔥', '😊'] as const;
const INITIAL_ANGLE = 200;
const ANGLE_OFFSET = 35;

const EMOJI_ITEMS: RadialMenuItem[] = EMOJI_OPTIONS.map((emoji, index) => ({
  id: emoji,
  angle: INITIAL_ANGLE + index * ANGLE_OFFSET,
  content: <span style={{fontSize: '20px', lineHeight: 1}}>{emoji}</span>,
}));

export type ChatInputAttachmentsConfig = {
  pending: PendingAttachment[];
  isUploading: boolean;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveAttachment: (sourceId: string) => void;
  accept: string;
};

export type ChatInputProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  isSubmitting?: boolean;
  showEmoji?: boolean;
  attachments?: ChatInputAttachmentsConfig;
  autoFocus?: boolean;
};

function ChatInput({
  value,
  onChange,
  onSubmit,
  placeholder = translate('Add a comment...'),
  disabled = false,
  isSubmitting = false,
  showEmoji = false,
  attachments,
  autoFocus = false,
}: ChatInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isBlocked =
    disabled || isSubmitting || (attachments?.isUploading ?? false);
  const canSend = value.trim() !== '' && !isBlocked;

  const hasPendingAttachments =
    (attachments?.isUploading ?? false) ||
    (attachments?.pending.length ?? 0) > 0;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter or Alt/Option+Enter → newline (let browser handle naturally)
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      if (!isBlocked && canSend) {
        onSubmit();
      }
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    onChange(value + emoji);
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.inputRow}>
        <TextareaAutosize
          className={styles.textarea}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          minRows={1}
          maxRows={5}
          disabled={disabled || isSubmitting}
          autoFocus={autoFocus}
        />
        {showEmoji ? (
          <RadialMenu
            interactionMode="click"
            items={EMOJI_ITEMS}
            selectedIds={[]}
            radius={60}
            offsetRotation={-45}
            onOptionClick={handleEmojiSelect}
            trigger={
              <Button
                size="small"
                appearance="neutral"
                mode="plain"
                className={styles.emojiButton}
                aria-label={translate('Add emoji')}
                disabled={isSubmitting}
              >
                😊
              </Button>
            }
          />
        ) : null}
        {attachments ? (
          <Button
            size="small"
            appearance="neutral"
            mode="plain"
            className={styles.attachButton}
            onClick={handleAttachClick}
            disabled={attachments.isUploading}
            aria-label={translate('Attach file')}
          >
            <Icon size="small" appearance="neutral" name="Navigation-Attach" />
          </Button>
        ) : null}
        <Button
          size="small"
          appearance="neutral"
          mode="filled"
          className={styles.sendButton}
          onClick={onSubmit}
          disabled={!canSend}
          aria-label={translate('Send')}
        >
          <Icon name="Navigation-Share" size="small" appearance="white" />
        </Button>
      </div>
      {hasPendingAttachments && attachments ? (
        <Stack className={styles.pendingAttachments}>
          {attachments.isUploading ? (
            <Stack direction="row" className={styles.pendingAttachmentRow}>
              <CircularProgress size={12} />
              <Body size="xs">{translate('Uploading...')}</Body>
            </Stack>
          ) : null}
          {attachments.pending.map((attachment) => (
            <Stack
              key={attachment.sourceId}
              direction="row"
              className={styles.pendingAttachmentRow}
            >
              <Body size="xs">{attachment.fileName}</Body>
              <Button
                size="small"
                appearance="neutral"
                mode="plain"
                className={styles.removeAttachmentButton}
                onClick={() =>
                  attachments.onRemoveAttachment(attachment.sourceId)
                }
                aria-label={translate('Remove attachment')}
              >
                <Icon
                  size="small"
                  appearance="neutral"
                  name="Actions-Close-Normal"
                />
              </Button>
            </Stack>
          ))}
        </Stack>
      ) : null}
      {attachments ? (
        <input
          ref={fileInputRef}
          type="file"
          accept={attachments.accept}
          multiple
          onChange={attachments.onFileChange}
          className={styles.hiddenInput}
        />
      ) : null}
    </div>
  );
}

export default ChatInput;
